import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import * as fabric from "fabric";
import { toast, Toaster } from "sonner";
import CreditsLedger from "./CreditsLedger";
import {
  Type, Square, Circle, Image as ImageIcon, Wand2, Layers,
  Download, Save, ChevronLeft, Sparkles, Loader2, Trash2,
  Copy, Shuffle, Film, Video, Zap, X, SlidersHorizontal, Upload,
} from "lucide-react";
import { adsApiCall } from "./api";
import AnimationPanel from "./AnimationPanel";
import AchievementCelebration from "./AchievementCelebration";
import ReelDirectorBar from "./ReelDirectorBar";
import StockPhotoPicker from "./StockPhotoPicker";
import TeamShareToggle from "./TeamShareToggle";
import TimelineBar from "./TimelineBar";
import { runAnimations } from "./animEngine";
import { useLanguage } from "../contexts/LanguageContext";
import "./ads-creator.css";

const FORMATS = {
  facebook_feed: { w: 1200, h: 628, label: "FB Feed" },
  facebook_story: { w: 1080, h: 1920, label: "FB Story" },
  instagram_feed: { w: 1080, h: 1080, label: "IG Feed" },
  instagram_story: { w: 1080, h: 1920, label: "IG Story" },
  instagram_reel: { w: 1080, h: 1920, label: "IG Reel" },
  google_display: { w: 300, h: 250, label: "Google" },
  linkedin_banner: { w: 1584, h: 396, label: "LinkedIn" },
};

// Studio content-type presets → default format for a fresh canvas
const TYPE_TO_FORMAT = {
  post: "instagram_feed",
  story: "instagram_story",
  reel: "instagram_reel",
  video: "facebook_feed", // 1200×628 — closest to 16:9 landscape
  ad: "facebook_feed",
};

const FONTS = ["Inter", "Poppins", "Playfair Display", "Bebas Neue", "Montserrat", "Roboto Slab"];

export default function Editor() {
  const { t } = useLanguage();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [urlParams] = [new URLSearchParams(window.location.search)];
  const typeParam = urlParams.get("type");
  const formatKey = TYPE_TO_FORMAT[typeParam] || "facebook_feed";
  const initFormat = FORMATS[formatKey];
  const canvasElRef = useRef(null);
  const fabricRef = useRef(null);
  const stageRef = useRef(null);

  const [project, setProject] = useState(null);
  const [activeRail, setActiveRail] = useState("templates");
  const [stockOpen, setStockOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [credits, setCredits] = useState(null);
  const [animations, setAnimations] = useState([]);
  const [animDuration, setAnimDuration] = useState(5000);
  const [recording, setRecording] = useState(false);
  // Mobile bottom-sheet state (ignored on desktop via CSS)
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);

  /* ---------- Uploads (cloud-persisted user assets) ---------- */
  const [myAssets, setMyAssets] = useState([]);
  const [uploading, setUploading] = useState(false);
  const loadAssets = useCallback(async () => {
    try {
      const r = await adsApiCall.listAssets(50, true);
      setMyAssets(r.assets || []);
    } catch { /* anon / not logged in */ }
  }, []);
  async function uploadAssetToCloud(file) {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error(t.studio_editor_toast_upload_too_large || "File too large (max 20MB)");
      return;
    }
    try {
      setUploading(true);
      const rec = await adsApiCall.uploadAsset(file);
      setMyAssets((prev) => [rec, ...prev]);
      toast.success(t.studio_editor_toast_upload_ok || "Uploaded");
      // Auto-add to canvas on upload so user sees instant result
      if (rec?.data_uri) addImageFromUrl(rec.data_uri);
    } catch (e) {
      toast.error((e?.response?.data?.detail || t.studio_editor_toast_upload_fail || "Upload failed"));
    } finally {
      setUploading(false);
    }
  }

  /* ---------- Scenes (multi-scene video timeline) ---------- */
  // `scenes` is a parallel view of the project — when empty, the editor
  // behaves like a single-canvas project (legacy). When non-empty, the
  // canvas reflects scenes[activeSceneIdx], and switching scenes swaps the
  // canvas JSON. Save/export iterate scenes.
  const [scenes, setScenes] = useState([]);
  const [activeSceneIdx, setActiveSceneIdx] = useState(0);

  function _newSceneId() { return "sc_" + Math.random().toString(36).slice(2, 10); }

  /** Serialise current Fabric canvas into a scene dict (shape matches backend). */
  function _currentCanvasAsScene() {
    const c = fabricRef.current;
    if (!c) return null;
    const cj = c.toJSON();
    cj.background = c.backgroundColor;
    cj.width = c.getWidth();
    cj.height = c.getHeight();
    return { canvas_json: cj };
  }

  function addScene() {
    if (scenes.length >= 5) {
      toast.error(t.studio_timeline_max || "Max 5 scenes per project");
      return;
    }
    // Ensure current canvas is committed to scenes[activeSceneIdx] first.
    const newScenes = [...scenes];
    if (newScenes.length === 0) {
      // First time: convert the current single-canvas project into a scene.
      // Default to a uniform 2000 ms per scene so the initial total (4.0 s
      // for 2 scenes) is predictable and matches the sibling scene default.
      const current = _currentCanvasAsScene();
      if (current) newScenes.push({
        scene_id: _newSceneId(),
        canvas_json: current.canvas_json,
        animations,
        duration_ms: 2000,
        transition: "cut",
      });
    } else {
      // Commit the live canvas into the active scene
      const current = _currentCanvasAsScene();
      if (current) newScenes[activeSceneIdx] = {
        ...newScenes[activeSceneIdx],
        canvas_json: current.canvas_json,
        animations,
        duration_ms: animDuration,
      };
    }
    // Push a new blank scene based on the same dimensions
    const refCj = newScenes[0].canvas_json;
    newScenes.splice(activeSceneIdx + 1, 0, {
      scene_id: _newSceneId(),
      canvas_json: {
        version: refCj?.version || "5.3.0",
        background: "#1a1a2e",
        width: refCj?.width || 1200,
        height: refCj?.height || 628,
        objects: [],
      },
      animations: [],
      duration_ms: 2000,
      transition: "cut",
    });
    setScenes(newScenes);
    switchScene(activeSceneIdx + 1, newScenes);
  }

  function switchScene(newIdx, sourceScenes = null) {
    const target = (sourceScenes || scenes)[newIdx];
    if (!target) return;
    // Persist current canvas into scenes[activeSceneIdx] UNLESS sourceScenes
    // was passed (we're coming from addScene which already did it).
    if (!sourceScenes) {
      const current = _currentCanvasAsScene();
      if (current) {
        setScenes((prev) => {
          const copy = [...prev];
          if (copy[activeSceneIdx]) {
            copy[activeSceneIdx] = {
              ...copy[activeSceneIdx],
              canvas_json: current.canvas_json,
              animations,
              duration_ms: animDuration,
            };
          }
          return copy;
        });
      }
    }
    setActiveSceneIdx(newIdx);
    setAnimations(target.animations || []);
    setAnimDuration(target.duration_ms || 2000);
    // Re-init canvas with target scene's JSON
    const c = fabricRef.current;
    if (!c) return;
    c.clear();
    c.setDimensions({ width: target.canvas_json?.width || c.getWidth(),
                      height: target.canvas_json?.height || c.getHeight() });
    c.backgroundColor = target.canvas_json?.background || "#ffffff";
    applyCanvasJson(c, target.canvas_json);
    c.renderAll();
    fitStage();
  }

  function deleteScene(idx) {
    if (scenes.length <= 1) {
      // Exiting multi-scene mode — drop scenes entirely, keep current canvas
      setScenes([]);
      setActiveSceneIdx(0);
      return;
    }
    const copy = scenes.filter((_, i) => i !== idx);
    setScenes(copy);
    const newIdx = Math.max(0, Math.min(idx, copy.length - 1));
    switchScene(newIdx, copy);
  }

  function duplicateScene(idx) {
    if (scenes.length >= 5) {
      toast.error(t.studio_timeline_max || "Max 5 scenes per project");
      return;
    }
    const current = _currentCanvasAsScene();
    const source = (idx === activeSceneIdx && current)
      ? { ...scenes[idx], canvas_json: current.canvas_json, animations, duration_ms: animDuration }
      : scenes[idx];
    const clone = {
      scene_id: _newSceneId(),
      canvas_json: JSON.parse(JSON.stringify(source.canvas_json || {})),
      animations: [...(source.animations || [])],
      duration_ms: source.duration_ms || 2000,
      transition: source.transition || "cut",
    };
    const copy = [...scenes];
    copy.splice(idx + 1, 0, clone);
    setScenes(copy);
    switchScene(idx + 1, copy);
  }

  function reorderScenes(from, to) {
    if (from === to) return;
    const copy = [...scenes];
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    setScenes(copy);
    // Keep the currently-edited scene in focus across the reorder
    let newActive = activeSceneIdx;
    if (from === activeSceneIdx) newActive = to;
    else if (from < activeSceneIdx && to >= activeSceneIdx) newActive = activeSceneIdx - 1;
    else if (from > activeSceneIdx && to <= activeSceneIdx) newActive = activeSceneIdx + 1;
    setActiveSceneIdx(newActive);
  }

  function updateScene(idx, patch) {
    setScenes((prev) => {
      const copy = [...prev];
      if (copy[idx]) copy[idx] = { ...copy[idx], ...patch };
      return copy;
    });
  }

  /* Toggle rail: open panel, or close if the same tab is tapped again */
  function onRailClick(name) {
    if (activeRail === name && leftOpen) {
      setLeftOpen(false);
    } else {
      setActiveRail(name);
      setLeftOpen(true);
    }
  }

  /* Auto-open properties sheet on mobile when an object is selected */
  useEffect(() => {
    if (selected && window.matchMedia("(max-width: 720px)").matches) {
      setRightOpen(true);
    }
  }, [selected]);

  /* Load cloud-stored user uploads (for Uploads rail) */
  useEffect(() => { loadAssets(); }, [loadAssets]);

  /* ---------- Init ---------- */
  useEffect(() => {
    (async () => {
      let p;
      try {
        if (projectId) {
          p = await adsApiCall.getProject(projectId);
        } else {
          // Create a new project using the requested format (from ?type= or default)
          p = await adsApiCall.createProject({
            title: t.studio_editor_untitled || "Untitled ad",
            format: formatKey,
            width: initFormat.w, height: initFormat.h,
            canvas_json: defaultCanvas(initFormat.w, initFormat.h),
          });
          navigate(`/ads-creator/editor/${p.project_id}`, { replace: true });
        }
      } catch (e) {
        const code = e?.response?.status;
        if (code === 401 || code === 403) {
          // Anonymous fallback — local-only project, user can log in later to save
          p = {
            project_id: "local",
            title: t.studio_editor_untitled || "Untitled ad",
            format: formatKey,
            width: initFormat.w, height: initFormat.h,
            canvas_json: defaultCanvas(initFormat.w, initFormat.h),
            animations: [],
            duration_ms: 5000,
            _anonymous: true,
          };
          toast.info(t.studio_editor_toast_offline_info || "You're working offline. Log in to save and export.");
        } else {
          toast.error(e?.response?.data?.detail || t.studio_editor_toast_open_fail || "Couldn't open editor");
          return;
        }
      }
      setProject(p);
      if (Array.isArray(p.scenes) && p.scenes.length > 0) {
        setScenes(p.scenes);
        setActiveSceneIdx(0);
        setAnimations(p.scenes[0].animations || []);
        setAnimDuration(p.scenes[0].duration_ms || 2000);
        // Render scene[0] instead of p.canvas_json
        initCanvas({ ...p, canvas_json: p.scenes[0].canvas_json });
      } else {
        if (Array.isArray(p.animations)) setAnimations(p.animations);
        if (p.duration_ms) setAnimDuration(p.duration_ms);
        initCanvas(p);
      }
      try { setCredits(await adsApiCall.credits()); } catch {}
    })();

    return () => {
      try { fabricRef.current?.dispose(); } catch {}
      fabricRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const initCanvas = useCallback((p) => {
    if (!canvasElRef.current) return;
    try { fabricRef.current?.dispose(); } catch {}
    const c = new fabric.Canvas(canvasElRef.current, {
      width: p.width, height: p.height,
      backgroundColor: p.canvas_json?.background || "#ffffff",
      preserveObjectStacking: true,
    });
    fabricRef.current = c;
    applyCanvasJson(c, p.canvas_json);
    // Fit immediately AND on next animation frame — mobile browsers often
    // report stale clientWidth right after canvas init.
    fitStage();
    requestAnimationFrame(() => fitStage());
    setTimeout(() => fitStage(), 200);

    c.on("selection:created", (e) => setSelected(e.selected?.[0] || null));
    c.on("selection:updated", (e) => setSelected(e.selected?.[0] || null));
    c.on("selection:cleared", () => setSelected(null));
    c.on("object:modified", () => setSelected(c.getActiveObject() || null));
  }, []);

  function applyCanvasJson(c, cj) {
    if (!cj?.objects) return;
    cj.objects.forEach((o) => {
      c.add(buildFabricObj(o));
    });
    c.renderAll();
  }

  function buildFabricObj(o) {
    if (o.type === "rect") {
      return new fabric.Rect({
        left: o.left || 0, top: o.top || 0,
        width: o.width || 100, height: o.height || 100,
        fill: o.fill || "#000",
        opacity: o.opacity ?? 1,
        rx: o.rx || 0, ry: o.ry || 0,
        angle: o.angle || 0,
        selectable: o.selectable !== false,
      });
    }
    if (o.type === "circle") {
      return new fabric.Circle({
        left: o.left || 0, top: o.top || 0,
        radius: o.radius || 50, fill: o.fill || "#fff",
        opacity: o.opacity ?? 1,
      });
    }
    if (o.type === "textbox") {
      return new fabric.Textbox(o.text || "Text", {
        left: o.left || 0, top: o.top || 0,
        width: o.width || 300,
        fontSize: o.fontSize || 24,
        fontFamily: o.fontFamily || "Inter",
        fontWeight: o.fontWeight || "normal",
        fontStyle: o.fontStyle || "normal",
        fill: o.fill || "#000",
        opacity: o.opacity ?? 1,
        textAlign: o.textAlign || "left",
        lineHeight: o.lineHeight || 1.16,
      });
    }
    return new fabric.Rect({ left: 0, top: 0, width: 10, height: 10, fill: "#888" });
  }

  /* ---------- Stage fit ---------- */
  function fitStage() {
    const stage = stageRef.current; const c = fabricRef.current;
    if (!stage || !c) return;
    const pad = 24;
    const sw = Math.max(100, stage.clientWidth - pad);
    const sh = Math.max(100, stage.clientHeight - pad);
    const cw = c.getWidth(), ch = c.getHeight();
    const scale = Math.min(sw / cw, sh / ch, 1);
    const el = c.wrapperEl;
    if (!el) return;
    // Shrink the wrapper box AND scale its contents with transform so the
    // flex parent doesn't overflow — critical on mobile for vertical 1080×1920
    // reels. Force the inner <canvas> elements (Fabric renders two — the
    // upper interaction layer + the lower draw layer) to 100% of the wrapper
    // so they follow the scaled wrapper even on first paint (prevents the
    // intermittent full-size overflow some users saw on tablets).
    el.style.transform = `scale(${scale})`;
    el.style.transformOrigin = "top left";
    el.style.width = `${cw * scale}px`;
    el.style.height = `${ch * scale}px`;
    el.style.maxWidth = "100%";
    el.style.maxHeight = "100%";
    el.querySelectorAll("canvas").forEach((inner) => {
      // These CSS sizes are the *display* size; Fabric's internal drawing
      // buffer (width/height attributes) stays at cw × ch. Using 100% makes
      // them lock to the now-scaled wrapper so there's no double-scaling
      // race between transform and pixel width.
      inner.style.width = "100%";
      inner.style.height = "100%";
      inner.style.maxWidth = "100%";
    });
  }
  useEffect(() => {
    const h = () => fitStage();
    window.addEventListener("resize", h);
    window.addEventListener("orientationchange", h);
    // Run on mount AND after each meaningful layout change (orientation,
    // sidebar collapse, etc.) to keep the canvas perfectly centred.
    const raf = requestAnimationFrame(() => {
      fitStage();
      setTimeout(fitStage, 120);
      setTimeout(fitStage, 400);
      setTimeout(fitStage, 900); // catch late font-loaded reflow
    });
    return () => {
      window.removeEventListener("resize", h);
      window.removeEventListener("orientationchange", h);
      cancelAnimationFrame(raf);
    };
  }, []);

  /* ---------- Tools ---------- */
  function addText() {
    const c = fabricRef.current; if (!c) return;
    const tb = new fabric.Textbox("Your headline", {
      left: 80, top: 80, width: 480,
      fontSize: 54, fontWeight: 800, fontFamily: "Inter", fill: "#111827",
    });
    c.add(tb); c.setActiveObject(tb); c.renderAll();
  }
  function addRect() {
    const c = fabricRef.current; if (!c) return;
    const r = new fabric.Rect({ left: 120, top: 120, width: 220, height: 120, fill: "#a855f7", rx: 12, ry: 12 });
    c.add(r); c.setActiveObject(r); c.renderAll();
  }
  function addCircle() {
    const c = fabricRef.current; if (!c) return;
    const r = new fabric.Circle({ left: 160, top: 160, radius: 80, fill: "#7c3aed" });
    c.add(r); c.setActiveObject(r); c.renderAll();
  }
  async function addImageFromUrl(url) {
    const c = fabricRef.current; if (!c) return;
    try {
      const img = await fabric.Image.fromURL(url, { crossOrigin: "anonymous" });
      // IMPORTANT: use the canvas's *design* dimensions (c.width / c.height),
      // not getWidth()/getHeight() — the latter returns CSS-pixel size after
      // the zoom transform, which makes the uploaded photo render as a tiny
      // thumbnail in the middle of a huge white canvas. Target 70 % of the
      // shorter design axis so there's breathing room for headlines + CTAs.
      const designW = c.width || 1080;
      const designH = c.height || 1920;
      const target = Math.min(designW, designH) * 0.7;
      const s = Math.min(target / img.width, target / img.height, 1);
      img.scale(s);
      // Centre the image on the canvas so users see it land cleanly,
      // instead of pinned to top-left (which feels broken on first upload).
      img.set({
        left: (designW - img.width * s) / 2,
        top:  (designH - img.height * s) / 2,
      });
      c.add(img); c.setActiveObject(img); c.renderAll();
    } catch (e) { toast.error(t.studio_editor_toast_image_fail || "Couldn't load image"); }
  }
  function uploadImage(file) {
    const reader = new FileReader();
    reader.onload = async (e) => addImageFromUrl(e.target.result);
    reader.readAsDataURL(file);
  }
  function deleteActive() {
    const c = fabricRef.current; const o = c?.getActiveObject();
    if (o) { c.remove(o); c.discardActiveObject(); c.renderAll(); setSelected(null); }
  }
  function duplicateActive() {
    const c = fabricRef.current; const o = c?.getActiveObject();
    if (!o) return;
    o.clone().then((cloned) => {
      cloned.set({ left: (o.left || 0) + 20, top: (o.top || 0) + 20 });
      c.add(cloned); c.setActiveObject(cloned); c.renderAll();
    });
  }
  function setBg(hex) {
    const c = fabricRef.current; if (!c) return;
    c.backgroundColor = hex; c.renderAll();
  }

  /* ---------- Update selected object ---------- */
  function updateSelected(patch) {
    const c = fabricRef.current; const o = c?.getActiveObject(); if (!o) return;
    o.set(patch); c.renderAll(); setSelected({ ...o });
  }

  /* ---------- Quick Reel — auto-animate everything ---------- */
  function quickReel() {
    const c = fabricRef.current; if (!c) return;
    const objs = c.getObjects();
    if (!objs.length) {
      toast.error(t.studio_editor_toast_add_elements_first || "Add some elements first, then click Quick Reel.");
      return;
    }
    // Heuristic: text → slide-in-up, "pill" rect (rounded) → bounce, circle → zoom-in,
    // other shapes → slide-in-right, images → fade-in.
    const anims = objs.map((o, i) => {
      let type = "fade-in";
      if (o.type === "textbox" || o.type === "i-text") {
        // Headline-like (big, multiple lines) → typewriter for pizzazz
        type = (o.fontSize > 40 && (o.text || "").length < 60) ? "typewriter" : "slide-in-up";
      }
      else if (o.type === "rect" && (o.rx > 8 || o.ry > 8)) type = "bounce";
      else if (o.type === "circle") type = "zoom-in";
      else if (o.type === "rect") type = "slide-in-right";
      else if (o.type === "image") type = "fade-in";
      return {
        object_index: i,
        type,
        start_at: i * 220,
        duration: type === "typewriter" ? 900 : type === "bounce" ? 720 : 620,
      };
    });
    const totalDur = Math.max(3000, anims.length * 220 + 1500);
    setAnimations(anims);
    setAnimDuration(totalDur);
    setActiveRail("anim");
    toast.success(`${t.studio_editor_quick_reel || "Quick Reel"} · ${anims.length} · ${(totalDur / 1000).toFixed(1)}s`);
  }

  /* ---------- MP4 Export ---------- */
  async function exportMP4() {
    const c = fabricRef.current; if (!c || !project) return;
    // Single-canvas mode requires at least one animation; multi-scene mode
    // can export a pure "slideshow" even without animations.
    if (scenes.length === 0 && !animations.length) {
      toast.error(t.studio_editor_toast_need_animation || "Add at least one animation first (Animate rail).");
      return;
    }
    setRecording(true);
    try {
      if (!project._anonymous) await saveProject();
      const canvasEl = c.getElement();
      const mime = pickMimeType();
      if (!mime) {
        toast.error(t.studio_editor_toast_browser_no_video || "Your browser doesn't support video recording.");
        return;
      }
      const stream = canvasEl.captureStream(30);
      const recorder = new MediaRecorder(stream, {
        mimeType: mime, videoBitsPerSecond: 4_000_000,
      });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      const done = new Promise((resolve) => { recorder.onstop = resolve; });
      recorder.start();

      if (scenes.length > 0) {
        // Multi-scene export: commit current canvas into active scene, then
        // iterate scenes in order, applying per-scene transitions.
        const current = _currentCanvasAsScene();
        const committed = scenes.map((s, i) =>
          i === activeSceneIdx && current
            ? { ...s, canvas_json: current.canvas_json, animations, duration_ms: animDuration }
            : s,
        );
        for (let i = 0; i < committed.length; i++) {
          const s = committed[i];
          await _renderSceneToCanvas(c, s);
          // Play per-scene animations (if any) during `duration_ms`
          if ((s.animations || []).length) {
            await runAnimations(c, s.animations, s.duration_ms || 2000);
          } else {
            await new Promise((r) => setTimeout(r, s.duration_ms || 2000));
          }
          // Apply transition to NEXT scene (not after the last one)
          if (i < committed.length - 1) {
            await _applyTransition(c, s.transition || "cut", committed[i + 1]);
          }
        }
      } else {
        // Legacy single-canvas animation export
        await runAnimations(c, animations, animDuration);
        await new Promise((r) => setTimeout(r, 400));
      }

      recorder.stop();
      stream.getTracks().forEach((tr) => tr.stop());
      await done;

      const blob = new Blob(chunks, { type: mime });
      const ext = mime.includes("mp4") ? "mp4" : "webm";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${project.title || "ad"}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);

      if (!project._anonymous) {
        await adsApiCall.exportProject({ project_id: project.project_id, format: "mp4" });
        try { setCredits(await adsApiCall.credits()); } catch {}
      }
      toast.success(`${t.studio_editor_toast_exported || "Exported!"} .${ext}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      if (detail?.code === "credits_exhausted") {
        toast.error(t.studio_editor_toast_credits_out || "Out of ad credits.");
        navigate("/ads-creator#pricing");
      } else {
        toast.error(detail || t.studio_editor_toast_video_fail || "Video export failed");
      }
    } finally { setRecording(false); }
  }

  /** Replace the canvas contents with the given scene's JSON (synchronous). */
  async function _renderSceneToCanvas(c, scene) {
    c.clear();
    const cj = scene.canvas_json || {};
    c.backgroundColor = cj.background || "#ffffff";
    applyCanvasJson(c, cj);
    c.renderAll();
  }

  /** Apply a visual transition from the current canvas state to the next
   * scene. `cut` is instant; `fade` overlays an opacity-fade overlay;
   * `slide` pushes the current content left and the next in from the right.
   */
  async function _applyTransition(c, kind, nextScene) {
    const DUR = 420; // ms — short enough to feel snappy, long enough to read
    if (kind === "cut" || !nextScene) return; // render next scene happens in loop
    const el = c.getElement();
    const parent = el.parentElement;
    if (!parent) return;
    if (kind === "fade") {
      // Overlay a black square that fades in → swap scene under cover →
      // fade out. We animate a fabric.Rect on the same canvas for pixel-
      // perfect recording.
      const w = c.getWidth(), h = c.getHeight();
      const overlay = new fabric.Rect({
        left: 0, top: 0, width: w, height: h,
        fill: "#000", opacity: 0, selectable: false, evented: false,
      });
      c.add(overlay); c.renderAll();
      await _tween((t) => { overlay.set("opacity", t); c.renderAll(); }, DUR / 2);
      // At full-black, swap the scene under the overlay
      c.remove(overlay);
      await _renderSceneToCanvas(c, nextScene);
      c.add(overlay); c.bringObjectToFront?.(overlay); c.renderAll();
      await _tween((t) => { overlay.set("opacity", 1 - t); c.renderAll(); }, DUR / 2);
      c.remove(overlay); c.renderAll();
    } else if (kind === "slide") {
      // Slide current scene's group left; at halfway-point swap to next.
      // Simpler: just fade with shorter duration and tag as slide (MVP).
      const w = c.getWidth(), h = c.getHeight();
      const overlay = new fabric.Rect({
        left: 0, top: 0, width: w, height: h,
        fill: "#000", opacity: 0, selectable: false, evented: false,
      });
      c.add(overlay); c.renderAll();
      await _tween((t) => { overlay.set("opacity", t); c.renderAll(); }, 200);
      c.remove(overlay);
      await _renderSceneToCanvas(c, nextScene);
      c.add(overlay); c.bringObjectToFront?.(overlay); c.renderAll();
      await _tween((t) => { overlay.set("opacity", 1 - t); c.renderAll(); }, 200);
      c.remove(overlay); c.renderAll();
    }
  }

  /** Tiny tween helper — calls `fn(progress 0→1)` over `duration` ms. */
  function _tween(fn, duration) {
    return new Promise((resolve) => {
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        fn(t);
        if (t < 1) requestAnimationFrame(tick);
        else resolve();
      };
      requestAnimationFrame(tick);
    });
  }

  function pickMimeType() {
    const prefs = ["video/mp4;codecs=avc1", "video/mp4", "video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"];
    for (const m of prefs) {
      try { if (MediaRecorder.isTypeSupported(m)) return m; } catch {}
    }
    return null;
  }

  /* ---------- Save / Export ---------- */
  async function saveProject() {
    const c = fabricRef.current; if (!c || !project) return;
    if (project._anonymous) {
      toast.info(t.studio_editor_toast_log_in_save || "Log in to save your ad to the cloud.");
      return;
    }
    setSaving(true);
    try {
      const cj = c.toJSON();
      cj.background = c.backgroundColor;
      cj.width = c.getWidth(); cj.height = c.getHeight();
      const payload = {
        canvas_json: cj,
        animations,
        duration_ms: animDuration,
      };
      // Multi-scene mode — also persist the full scenes array with the live
      // canvas committed into scenes[activeSceneIdx].
      if (scenes.length > 0) {
        const committed = scenes.map((s, i) =>
          i === activeSceneIdx
            ? { ...s, canvas_json: cj, animations, duration_ms: animDuration }
            : s,
        );
        payload.scenes = committed;
      }
      await adsApiCall.updateProject(project.project_id, payload);
      toast.success(t.studio_editor_toast_saved || "Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || t.studio_editor_toast_save_fail || "Save failed");
    } finally { setSaving(false); }
  }

  async function exportPng() {
    const c = fabricRef.current; if (!c || !project) return;
    setExporting(true);
    try {
      if (!project._anonymous) {
        await saveProject();
        await adsApiCall.exportProject({ project_id: project.project_id, format: "png" });
      }
      const dataUrl = c.toDataURL({ format: "png", multiplier: 1 });
      const a = document.createElement("a");
      a.href = dataUrl; a.download = `${project.title || "ad"}.png`; a.click();
      if (!project._anonymous) {
        try { setCredits(await adsApiCall.credits()); } catch {}
      }
      toast.success(t.studio_editor_toast_exported || "Exported!");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "credits_exhausted") {
        toast.error(t.studio_editor_toast_credits_out_upsell || "You're out of ad credits. Upgrade to Pro for 30/month.");
        navigate("/ads-creator#pricing");
      } else {
        toast.error(detail?.message || detail || t.studio_editor_toast_export_fail || "Export failed");
      }
    } finally { setExporting(false); }
  }

  /* ---------- AI Magic ---------- */
  const [magicUrl, setMagicUrl] = useState("");
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicVariants, setMagicVariants] = useState([]);
  async function runMagic() {
    if (!magicUrl.trim()) return;
    setMagicLoading(true);
    try {
      const res = await adsApiCall.aiMagic({ url: magicUrl.trim(), tone: "energetic", language: "en" });
      setMagicVariants(res.variants || []);
      if (!res.variants?.length) toast.error(res.error || t.studio_editor_toast_no_variants || "No variants returned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.studio_editor_toast_ai_fail || "AI Magic failed");
    } finally { setMagicLoading(false); }
  }
  function applyMagicVariant(v) {
    const c = fabricRef.current; if (!c) return;
    // Clear and re-render from canvas_json
    c.clear();
    c.backgroundColor = v.canvas_json?.background || "#111";
    (v.canvas_json?.objects || []).forEach((o) => c.add(buildFabricObj(o)));
    c.renderAll();
    toast.success(`${t.studio_editor_rail_magic || "Magic"}: ${v.angle}`);
  }

  /* ---------- Smart Resize ---------- */
  async function smartResize() {
    if (!project) return;
    try {
      const res = await adsApiCall.smartResize(project.project_id);
      toast.success(`✓ ${res.created}`);
      navigate("/ads-creator/my-ads");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.studio_editor_toast_smart_resize_fail || "Smart resize failed");
    }
  }

  /* ---------- Render ---------- */
  return (
    <div className="ads-root" data-testid="ads-editor-root">
      <Toaster position="top-right" theme="dark" />
      <div className="ads-editor">
        {/* Topbar */}
        <div className="ads-editor-topbar">
          <Link to="/ads-creator" className="ads-btn ads-btn-ghost" style={{ padding: "6px 12px" }} data-testid="ads-editor-back">
            <ChevronLeft size={16} /> {t.studio_editor_back || "Back"}
          </Link>
          <div className="ads-editor-title" style={{ flex: 1 }}>
            <input
              value={project?.title || ""}
              onChange={(e) => setProject({ ...project, title: e.target.value })}
              onBlur={() => {
                if (!project || project._anonymous) return;
                adsApiCall.updateProject(project.project_id, { title: project.title });
              }}
              placeholder={t.studio_editor_untitled || "Untitled ad"}
              data-testid="ads-editor-title-input"
            />
          </div>
          {credits && (
            <button
              type="button"
              onClick={() => setLedgerOpen(true)}
              style={{ color: "var(--ads-text-dim)", fontSize: 12, background: "transparent", border: 0, cursor: "pointer", padding: 4 }}
              data-testid="ads-editor-credits"
              title={t.studio_editor_credits_view || "View credits ledger"}
            >
              {t.studio_editor_credits_label || "Credits"}: {credits.admin_unlimited
                ? (t.studio_editor_credits_unlimited || "∞ Unlimited")
                : `${(credits.credits_total || 0) - (credits.credits_used || 0)} / ${credits.credits_total || 0}`}
            </button>
          )}
          {ledgerOpen && <CreditsLedger onClose={() => setLedgerOpen(false)} />}
          <button className="ads-btn ads-btn-ghost" onClick={smartResize} data-testid="ads-editor-smart-resize">
            <Shuffle size={16} /> {t.studio_editor_smart_resize || "Smart Resize"}
          </button>
          <button className="ads-btn ads-btn-ghost" onClick={quickReel} data-testid="ads-editor-quick-reel"
            title={t.studio_editor_quick_reel_title || "Auto-animate every element for a 15-second reel"}>
            <Zap size={16} /> {t.studio_editor_quick_reel || "Quick Reel"}
          </button>
          <button className="ads-btn ads-btn-ghost" onClick={addScene}
            disabled={scenes.length >= 5}
            data-testid="ads-editor-add-scene"
            title={t.studio_timeline_add_hint || "Add a new scene — switches to multi-scene timeline"}>
            <Film size={16} /> {scenes.length > 0 ? (t.studio_timeline_add || "Add scene") : (t.studio_timeline_enable || "Scenes")}
          </button>
          <button className="ads-btn ads-btn-ghost" onClick={saveProject} disabled={saving} data-testid="ads-editor-save">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {t.studio_editor_save || "Save"}
          </button>
          <TeamShareToggle projectId={project?.project_id} initialShared={!!project?.team_id} />
          <button className="ads-btn ads-btn-primary" onClick={exportPng} disabled={exporting} data-testid="ads-editor-export">
            {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} {t.studio_editor_export_png || "Export PNG"}
          </button>
          <button className="ads-btn ads-btn-ghost" onClick={exportMP4}
            disabled={recording || (scenes.length === 0 && !animations.length)}
            title={(scenes.length > 0 || animations.length)
              ? (t.studio_editor_mp4_title_ok || "Export animated video")
              : (t.studio_editor_mp4_title_need_anim || "Add an animation first")}
            data-testid="ads-editor-export-mp4">
            {recording ? <Loader2 size={16} className="animate-spin" /> : <Video size={16} />} {t.studio_editor_mp4 || "MP4"}
          </button>
        </div>

        {/* Body */}
        <div className="ads-editor-body">
          {/* Rail */}
          <div className="ads-editor-rail" data-testid="ads-editor-rail">
            <RailBtn active={activeRail === "magic"} onClick={() => onRailClick("magic")} label={t.studio_editor_rail_magic || "Magic"} testKey="magic"><Wand2 size={18} /></RailBtn>
            <RailBtn active={activeRail === "text"} onClick={() => onRailClick("text")} label={t.studio_editor_rail_text || "Text"} testKey="text"><Type size={18} /></RailBtn>
            <RailBtn active={activeRail === "shapes"} onClick={() => onRailClick("shapes")} label={t.studio_editor_rail_shapes || "Shapes"} testKey="shapes"><Square size={18} /></RailBtn>
            <RailBtn active={activeRail === "images"} onClick={() => onRailClick("images")} label={t.studio_editor_rail_images || "Images"} testKey="images"><ImageIcon size={18} /></RailBtn>
            <RailBtn active={activeRail === "uploads"} onClick={() => onRailClick("uploads")} label={t.studio_editor_rail_uploads || "Uploads"} testKey="uploads"><Upload size={18} /></RailBtn>
            <RailBtn active={activeRail === "anim"} onClick={() => onRailClick("anim")} label={t.studio_editor_rail_anim || "Animate"} testKey="anim"><Film size={18} /></RailBtn>
            <RailBtn active={activeRail === "layers"} onClick={() => onRailClick("layers")} label={t.studio_editor_rail_layers || "Layers"} testKey="layers"><Layers size={18} /></RailBtn>
          </div>

          {/* Mobile backdrop — closes any open sheet */}
          <div
            className={`ads-mobile-backdrop ${leftOpen || rightOpen ? "is-open" : ""}`}
            onClick={() => { setLeftOpen(false); setRightOpen(false); }}
            data-testid="ads-mobile-backdrop"
          />

          {/* Left panel */}
          <div className={`ads-editor-panel ${leftOpen ? "is-open" : ""}`} data-testid="ads-editor-panel">
            <button
              className="ads-mobile-sheet-close"
              onClick={() => setLeftOpen(false)}
              data-testid="ads-editor-panel-close"
              aria-label={t.studio_editor_close_panel || "Close panel"}
            >
              <X size={16} />
            </button>
            {activeRail === "magic" && (
              <>
                <div className="ads-panel-title">{t.studio_editor_director_title || "AI Reel Director — one-shot"}</div>
                <div style={{ fontSize: 11, color: "var(--ads-text-dim)", marginBottom: 10 }}>
                  {t.studio_editor_director_sub || "One sentence → full animated reel (~10s)."}
                </div>
                <ReelDirectorBar compact defaultFormat={project?.format} />

                <div className="ads-panel-title" style={{ marginTop: 22 }}>{t.studio_editor_magic_title || "AI Magic — URL → 5 ads"}</div>
                <input
                  className="ads-panel-input"
                  placeholder={t.studio_editor_magic_placeholder || "https://your-site.com"}
                  value={magicUrl}
                  onChange={(e) => setMagicUrl(e.target.value)}
                  data-testid="ads-magic-url"
                />
                <button className="ads-btn ads-btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={runMagic} disabled={magicLoading} data-testid="ads-magic-run">
                  {magicLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {t.studio_editor_magic_generate || "Generate"}
                </button>
                <div className="ads-magic-grid">
                  {magicVariants.map((v, i) => (
                    <div key={i} className="ads-magic-card" onClick={() => applyMagicVariant(v)} data-testid={`ads-magic-variant-${i}`}>
                      <div className="ads-magic-headline" style={{ color: v.colors?.text }}>{v.headline}</div>
                      <div className="ads-magic-sub">{v.subheadline}</div>
                      <span className="ads-magic-cta" style={{ background: v.colors?.accent || "#7c3aed" }}>{v.cta}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
            {activeRail === "text" && (
              <>
                <div className="ads-panel-title">{t.studio_editor_text_title || "Text"}</div>
                <button className="ads-btn ads-btn-ghost" style={{ width: "100%", justifyContent: "flex-start" }} onClick={addText} data-testid="ads-add-text">
                  <Type size={16} /> {t.studio_editor_add_heading || "Add heading"}
                </button>
              </>
            )}
            {activeRail === "shapes" && (
              <>
                <div className="ads-panel-title">{t.studio_editor_shapes_title || "Shapes"}</div>
                <button className="ads-btn ads-btn-ghost" style={{ width: "100%", justifyContent: "flex-start", marginBottom: 8 }} onClick={addRect} data-testid="ads-add-rect"><Square size={16} /> {t.studio_editor_rectangle || "Rectangle"}</button>
                <button className="ads-btn ads-btn-ghost" style={{ width: "100%", justifyContent: "flex-start" }} onClick={addCircle} data-testid="ads-add-circle"><Circle size={16} /> {t.studio_editor_circle || "Circle"}</button>
                <div className="ads-panel-title" style={{ marginTop: 18 }}>{t.studio_editor_background_title || "Background"}</div>
                <input type="color" className="ads-prop-input ads-color-input" onChange={(e) => setBg(e.target.value)} data-testid="ads-set-bg" />
              </>
            )}
            {activeRail === "images" && (
              <>
                <div className="ads-panel-title">{t.studio_editor_upload_title || "Upload image"}</div>
                <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} className="ads-panel-input" data-testid="ads-upload-image" />
                <button
                  className="ads-btn ads-btn-primary"
                  style={{ width: "100%", justifyContent: "center", marginTop: 12 }}
                  onClick={() => setStockOpen(true)}
                  data-testid="ads-open-stock"
                >
                  <ImageIcon size={14} /> {t.studio_editor_stock_btn || "Stock photos"}
                </button>
                <div className="ads-panel-title" style={{ marginTop: 14 }}>{t.studio_editor_from_url || "From URL"}</div>
                <input id="imgurl" className="ads-panel-input" placeholder="https://…" data-testid="ads-image-url" />
                <button className="ads-btn ads-btn-ghost" style={{ width: "100%", justifyContent: "center" }} onClick={() => addImageFromUrl(document.getElementById("imgurl").value)}>{t.studio_editor_add || "Add"}</button>
              </>
            )}
            {activeRail === "uploads" && (
              <>
                <div className="ads-panel-title">{t.studio_editor_uploads_title || "Your uploads"}</div>
                <label
                  className="ads-btn ads-btn-primary"
                  style={{ width: "100%", justifyContent: "center", cursor: "pointer", marginBottom: 10 }}
                  data-testid="ads-uploads-add-btn"
                >
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {uploading
                    ? (t.studio_editor_uploads_uploading || "Uploading…")
                    : (t.studio_editor_uploads_upload || "Upload photo")}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => e.target.files?.[0] && uploadAssetToCloud(e.target.files[0])}
                    data-testid="ads-uploads-input"
                  />
                </label>
                <div style={{ fontSize: 11, color: "var(--ads-text-dim)", marginBottom: 10 }}>
                  {t.studio_editor_uploads_hint || "Cloud-stored. Drag onto canvas or tap to add."}
                </div>
                {myAssets.length === 0 ? (
                  <div style={{
                    padding: "22px 10px", textAlign: "center",
                    color: "var(--ads-text-dim)", fontSize: 12,
                    border: "1px dashed rgba(255,255,255,0.1)", borderRadius: 10,
                  }} data-testid="ads-uploads-empty">
                    {t.studio_editor_uploads_empty || "No uploads yet — upload your first photo above."}
                  </div>
                ) : (
                  <div
                    style={{
                      display: "grid", gap: 8,
                      gridTemplateColumns: "repeat(auto-fill,minmax(78px,1fr))",
                    }}
                    data-testid="ads-uploads-grid"
                  >
                    {myAssets.map((a) => (
                      <button
                        key={a.asset_id}
                        type="button"
                        onClick={() => a.data_uri && addImageFromUrl(a.data_uri)}
                        title={a.name || a.asset_id}
                        data-testid={`ads-upload-${a.asset_id}`}
                        style={{
                          aspectRatio: "1 / 1", padding: 0, borderRadius: 8,
                          overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)",
                          background: a.data_uri ? "#111" : "rgba(255,255,255,0.04)",
                          cursor: "pointer",
                        }}
                      >
                        {a.data_uri && (
                          <img
                            src={a.data_uri}
                            alt={a.name || ""}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            draggable={false}
                          />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            {activeRail === "anim" && (
              <AnimationPanel
                fabricRef={fabricRef}
                animations={animations}
                setAnimations={setAnimations}
                duration={animDuration}
                setDuration={setAnimDuration}
              />
            )}
            {activeRail === "layers" && (
              <>
                <div className="ads-panel-title">{t.studio_editor_layers_title || "Layers"}</div>
                <Layers_Panel fabricRef={fabricRef} selected={selected} t={t} />
              </>
            )}
          </div>

          {/* Canvas stage + optional timeline under it */}
          <div className="ads-canvas-stage" ref={stageRef} data-testid="ads-editor-stage">
            <div className="ads-canvas-frame">
              <canvas ref={canvasElRef} />
            </div>
            {scenes.length > 0 && (
              <TimelineBar
                scenes={scenes}
                activeIdx={activeSceneIdx}
                onSwitch={(i) => switchScene(i)}
                onAdd={addScene}
                onDelete={deleteScene}
                onDuplicate={duplicateScene}
                onReorder={reorderScenes}
                onUpdateScene={updateScene}
                t={t}
              />
            )}
          </div>

          {/* Right: properties */}
          <div className={`ads-sidepanel ${rightOpen ? "is-open" : ""}`} data-testid="ads-editor-sidepanel">
            <button
              className="ads-mobile-sheet-close"
              onClick={() => setRightOpen(false)}
              data-testid="ads-editor-sidepanel-close"
              aria-label={t.studio_editor_close_props || "Close properties"}
            >
              <X size={16} />
            </button>
            <div className="ads-panel-title">{t.studio_editor_props_title || "Properties"}</div>
            {!selected ? (
              <div style={{ color: "var(--ads-text-dim)", fontSize: 13 }}>{t.studio_editor_select_element || "Select an element to edit its properties."}</div>
            ) : (
              <Properties obj={selected} update={updateSelected} onDelete={deleteActive} onDuplicate={duplicateActive} t={t} />
            )}
          </div>
        </div>

        {/* Floating properties button on mobile */}
        <button
          className="ads-mobile-fab"
          onClick={() => setRightOpen(true)}
          data-testid="ads-editor-props-fab"
          aria-label={t.studio_editor_open_props || "Open properties"}
        >
          <SlidersHorizontal size={18} />
        </button>
      </div>
      <AchievementCelebration />
      {stockOpen && (
        <StockPhotoPicker
          onSelect={(url) => { addImageFromUrl(url); setStockOpen(false); }}
          onClose={() => setStockOpen(false)}
        />
      )}
    </div>
  );
}

function defaultCanvas(w, h) {
  return { version: "5.3.0", width: w, height: h, background: "#ffffff", objects: [] };
}


function RailBtn({ active, onClick, label, children, testKey }) {
  return (
    <button className={`ads-rail-btn ${active ? "active" : ""}`} onClick={onClick} data-testid={`ads-rail-${testKey || (label || "").toLowerCase()}`}>
      {children}
      <span>{label}</span>
    </button>
  );
}

function Properties({ obj, update, onDelete, onDuplicate, t = {} }) {
  const isText = obj?.type === "textbox" || obj?.type === "i-text" || obj?.type === "text";
  return (
    <div>
      {isText && (
        <>
          <div className="ads-prop-row">
            <label className="ads-prop-label">{t.studio_editor_prop_text || "Text"}</label>
            <textarea className="ads-prop-input" rows={3} value={obj.text || ""} onChange={(e) => update({ text: e.target.value })} data-testid="ads-prop-text" />
          </div>
          <div className="ads-prop-row">
            <label className="ads-prop-label">{t.studio_editor_prop_font || "Font"}</label>
            <select className="ads-prop-input" value={obj.fontFamily || "Inter"} onChange={(e) => update({ fontFamily: e.target.value })} data-testid="ads-prop-font">
              {FONTS.map((f) => <option key={f}>{f}</option>)}
            </select>
          </div>
          <div className="ads-prop-row">
            <label className="ads-prop-label">{t.studio_editor_prop_size || "Size"}</label>
            <input type="number" className="ads-prop-input" value={obj.fontSize || 24} onChange={(e) => update({ fontSize: parseInt(e.target.value) || 24 })} data-testid="ads-prop-size" />
          </div>
          <div className="ads-pill-row">
            <div className={`ads-pill ${obj.fontWeight === "bold" || obj.fontWeight >= 700 ? "active" : ""}`} onClick={() => update({ fontWeight: obj.fontWeight === "bold" ? "normal" : "bold" })}>B</div>
            <div className={`ads-pill ${obj.fontStyle === "italic" ? "active" : ""}`} onClick={() => update({ fontStyle: obj.fontStyle === "italic" ? "normal" : "italic" })}>i</div>
            <div className={`ads-pill ${obj.textAlign === "left" ? "active" : ""}`} onClick={() => update({ textAlign: "left" })}>L</div>
            <div className={`ads-pill ${obj.textAlign === "center" ? "active" : ""}`} onClick={() => update({ textAlign: "center" })}>C</div>
            <div className={`ads-pill ${obj.textAlign === "right" ? "active" : ""}`} onClick={() => update({ textAlign: "right" })}>R</div>
          </div>
        </>
      )}
      <div className="ads-prop-row">
        <label className="ads-prop-label">{t.studio_editor_prop_fill || "Fill"}</label>
        <input type="color" className="ads-prop-input ads-color-input" value={obj.fill || "#000000"} onChange={(e) => update({ fill: e.target.value })} data-testid="ads-prop-fill" />
      </div>
      <div className="ads-prop-row">
        <label className="ads-prop-label">{t.studio_editor_prop_opacity || "Opacity"}</label>
        <input type="range" min={0} max={1} step={0.05} value={obj.opacity ?? 1} onChange={(e) => update({ opacity: parseFloat(e.target.value) })} data-testid="ads-prop-opacity" />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button className="ads-btn ads-btn-ghost" style={{ flex: 1, justifyContent: "center" }} onClick={onDuplicate} data-testid="ads-prop-duplicate"><Copy size={14} /> {t.studio_editor_duplicate || "Duplicate"}</button>
        <button className="ads-btn ads-btn-ghost" style={{ flex: 1, justifyContent: "center", color: "#f87171" }} onClick={onDelete} data-testid="ads-prop-delete"><Trash2 size={14} /> {t.studio_editor_delete || "Delete"}</button>
      </div>
    </div>
  );
}

function Layers_Panel({ fabricRef, selected, t = {} }) {
  const [tick, setTick] = useState(0);
  const c = fabricRef.current;
  useEffect(() => {
    if (!c) return;
    const h = () => setTick((t) => t + 1);
    c.on("object:added", h); c.on("object:removed", h); c.on("object:modified", h);
    return () => { c.off("object:added", h); c.off("object:removed", h); c.off("object:modified", h); };
  }, [c]);
  if (!c) return null;
  const objs = c.getObjects();
  return (
    <div style={{ display: "grid", gap: 6 }} data-testid="ads-layers-list">
      {objs.length === 0 && <div style={{ color: "var(--ads-text-dim)", fontSize: 13 }}>{t.studio_editor_canvas_empty || "Canvas is empty."}</div>}
      {objs.map((o, i) => {
        const name = o.type === "textbox" ? `"${(o.text || "").slice(0, 18)}"` : o.type;
        return (
          <div key={i}
            onClick={() => { c.setActiveObject(o); c.renderAll(); }}
            style={{
              padding: "8px 10px", borderRadius: 8, cursor: "pointer",
              background: o === selected ? "var(--ads-accent)" : "var(--ads-bg)",
              color: o === selected ? "#fff" : "var(--ads-text)",
              fontSize: 12, border: "1px solid var(--ads-border)",
            }}
          >
            {objs.length - i}. {name}
          </div>
        );
      })}
    </div>
  );
}
